import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from '@/contexts/AuthContext';
import { checkAndRepairUserBadges } from '@/lib/repairAllUserBadges';

export default function BadgeDebugTool() {
  const { user } = useAuth();
  const [isChecking, setIsChecking] = useState(false);
  
  const handleBadgeCheck = async () => {
    if (!user) {
      toast({
        title: "Not logged in",
        description: "You need to be logged in to check your badges.",
        variant: "destructive",
      });
      return;
    }
    
    setIsChecking(true);
    toast({
      title: "Checking Badges",
      description: "Verifying your badges...",
    });
    
    try {
      const badgeResult = await checkAndRepairUserBadges(user.id);
      
      if (badgeResult.success) {
        if (badgeResult.newlyEarnedBadges.length > 0) {
          // If there's just one badge, show a detailed notification
          if (badgeResult.newlyEarnedBadges.length === 1) {
            const badge = badgeResult.newlyEarnedBadges[0];
            toast({
              title: `${badge.icon} New Badge Earned: ${badge.name}`,
              description: badge.description,
              variant: "default"
            });
          } 
          // If there are multiple badges, show a summary
          else {
            toast({
              title: "🎉 New Badges Earned!",
              description: `You've earned ${badgeResult.newlyEarnedBadges.length} new badges! Check your profile to see them.`,
              variant: "default"
            });
          }
        } else {
          toast({
            title: "Badges Checked",
            description: "All your badges are up to date.",
            variant: "default"
          });
        }
      } else {
        toast({
          title: "Badge Check Failed",
          description: "There was an error checking your badges.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error checking badges:', error);
      toast({
        title: "Badge Check Error",
        description: "An unexpected error occurred while checking your badges.",
        variant: "destructive"
      });
    } finally {
      setIsChecking(false);
    }
  };
  
  if (!user) {
    return null; // Don't show the component if user is not logged in
  }
  
  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle>Badge Debug Tool</CardTitle>
        <CardDescription>Check and repair your badges</CardDescription>
      </CardHeader>
      <CardContent>
        <Button 
          onClick={handleBadgeCheck}
          disabled={isChecking}
          className="w-full"
        >
          {isChecking ? "Checking Badges..." : "Verify My Badges"}
        </Button>
      </CardContent>
    </Card>
  );
} 